import { ExerciseNavigationHeader } from './ExerciseNavigationHeader';
import { HearingExerciseText } from '@/components/HearingExercise/text';
import { LesenTextExercise } from '@/components/LesenTextExercise';
import { SprechenTextExercise } from '@/components/SpeakingExercise/text';
import {
  useResponsiveFontSize,
  useResponsiveHeight,
  useResponsiveWidth,
} from '@/components/Util/reactResponsiveDimensions';
import { WritingTextExercise } from '@/components/WritingExercise/text';
import colors from '@/config/colors';
import { isPatientApp } from '@/config/constants';
import { AudioContextProvider } from '@/containers/AudioContext';
import { useAufgabenExerciseText } from '@/hooks/useAufgabenExerciseText';
import FontAwesome from '@expo/vector-icons/FontAwesome';
import { useNavigation, useRoute } from '@react-navigation/native';
import cloneDeep from 'lodash/cloneDeep';
import { useCallback, useMemo, useRef } from 'react';
import { PanResponder, Platform, StyleSheet, View } from 'react-native';
import * as Progress from 'react-native-progress';
import { SafeAreaView } from 'react-native-safe-area-context';
import { TASKS } from './constants';

function Exercises() {
  const responsiveFontSize =
    Platform.OS === 'web'
      ? useResponsiveFontSize(1.25)
      : useResponsiveFontSize(1.3);
  const starIconSize = useResponsiveFontSize(2);
  const isTouched = useRef(false);
  const { params } = useRoute();

  const {
    setId,
    patientId,
    localWordset,
    isSelf,
    selectedPatient,
    activeSets,
    activatedExerciseModalities,
  } = params ?? {
    setId: '',
    patientId: '',
    setType: '',
    localWordset: {},
    isSelf: false,
    selectedPatient: null,
    activeSets: [],
  };

  const goHome = () => {
    if (isPatientApp) navigate('Home');
    else
      navigate('SelfTraining', {
        selectedPatient,
        activeSets,
      });
  };

  const {
    state,
    scope,
    PAGER_OPTIONS,
    back,
    showQuestions,
    skip,
    touched, //?  useTimeService return value
    goSelect,
    checkProgress,
  } = useAufgabenExerciseText(
    setId,
    localWordset,
    patientId,
    goHome,
    isSelf,
    activatedExerciseModalities,
  );

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponderCapture: () => {
        if (!isSelf) return;
        touched();
        isTouched.current = true;
      },
    }),
  ).current;

  const dailyGoalActivated = state.settings?.activateDailyGoal;

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: '#F2F2F2',
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },

    text: {
      marginHorizontal: 5,
      fontWeight: '700',
      fontSize: responsiveFontSize,
    },
    textWhite: {
      color: 'white',
    },
    btnContainer: {
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 5,
      marginLeft: 5,
    },
    darkBtn: {
      backgroundColor: '#3a3a3a',
    },
    primaryBtn: {
      backgroundColor: '#51bf92',
    },
    body: {
      flex: 1,
    },
    pager: {
      marginVertical: 5,
    },
    circle: {
      height: useResponsiveWidth(1),
      width: useResponsiveWidth(1),
      borderRadius: 50,
      backgroundColor: '#808080',
      marginHorizontal: 1,
    },
    progressBarStyle: {
      height: useResponsiveHeight(2),
      width: '50%',
    },
    activeCircle: {
      backgroundColor: '#3a3a3a',
    },
    isMobile: {
      transform: [{ scaleY: -1 }, { scaleX: -1 }],
    },
    isUpsideDown: {
      transform: [{ scaleY: -1 }, { scaleX: -1 }],
    },
  });

  const { goBack, navigate } = useNavigation();

  const activeExercise = scope?.exercise;
  const activeExerciseName = activeExercise?.name;
  const settings = state?.settings?.[activeExerciseName];
  const progressBarValue = (state.currentExercise + 1) / state.exercises.length;

  const exerciseClassIcon = useMemo(() => {
    return (
      TASKS.text.find(
        (task) => task.exerciseSettingsKey == activeExerciseName,
      ) ?? {
        label: '',
        iconName: '',
        iconLibrary: '',
        exerciseSettingsKey: '',
      }
    );
  }, [activeExerciseName]);

  const PAGER_RENDERER = useMemo(() => {
    return PAGER_OPTIONS.map((option) => {
      const isActive = option == activeExercise?.id;
      return (
        <View
          style={[styles.circle, isActive && styles.activeCircle]}
          key={option}
        />
      );
    });
  }, [JSON.stringify(PAGER_OPTIONS), activeExercise?.id]);

  const handlePressSkip = () => {
    skip();
  };

  const handlePressBack = useCallback(() => {
    if (isSelf) {
      if (isTouched.current) goSelect();
      else goHome();
    } else {
      goBack();
    }
  }, [goSelect]);

  let iterationKey = activeExercise
    ? activeExercise.uniqueExercisePrepareDate
    : null;

  const ActiveComponent = {
    Verstehen: (
      <HearingExerciseText
        id={iterationKey}
        checkProgress={checkProgress}
        showQuestions={showQuestions}
        back={back}
        skip={handlePressSkip}
        exercise={cloneDeep(activeExercise)}
        settings={settings}
        exerciseState={scope?.exerciseState}
        audioUrl={scope?.audioUrl}
        currentExercise={state?.currentExercise}
      />
    ),
    Lesen: (
      <LesenTextExercise
        checkProgress={checkProgress}
        showQuestions={showQuestions}
        back={back}
        skip={handlePressSkip}
        exercise={cloneDeep(activeExercise)}
        settings={settings}
        exerciseState={scope?.exerciseState}
        currentExercise={state?.currentExercise}
      />
    ),
    Sprechen: (
      <SprechenTextExercise
        id={iterationKey}
        checkProgress={checkProgress}
        audioUrl={scope?.exercise?.textNormalAudioURL}
        showQuestions={showQuestions}
        back={back}
        skip={handlePressSkip}
        exercise={cloneDeep(activeExercise)}
        settings={settings}
        exerciseState={scope?.exerciseState}
        currentExercise={state?.currentExercise}
      />
    ),
    Schreiben: (
      <WritingTextExercise
        checkProgress={checkProgress}
        skip={handlePressSkip}
        exercise={cloneDeep(activeExercise)}
        settings={settings}
        currentExercise={state?.currentExercise}
        isSelf={isSelf}
      />
    ),
  };

  return (
    <>
      <ExerciseNavigationHeader
        isSelf={isSelf}
        exerciseClass={exerciseClassIcon}
        onPressHome={handlePressBack}
        onPressSkip={handlePressSkip}
        activeExerciseClass="text"
        skipTitle="Überspringen"
      />
      <View style={styles.container}>
        <SafeAreaView style={styles.body} {...panResponder.panHandlers}>
          {scope?.exercise && (
            <View style={styles.body} key={iterationKey}>
              {ActiveComponent[activeExerciseName]}
            </View>
          )}
          {!isSelf ? (
            <View style={[styles.row, styles.pager]}>{PAGER_RENDERER}</View>
          ) : (
            <View style={[styles.row, styles.pager]}>
              {dailyGoalActivated ? (
                <>
                  <Progress.Bar
                    width={null}
                    borderRadius={8}
                    borderWidth={0}
                    height={24}
                    key="progress"
                    color={colors.primary}
                    progress={state.currentProgress}
                    style={styles.progressBarStyle}
                    className="bg-green-neolexon/30"
                  />
                  <FontAwesome
                    name="star"
                    size={starIconSize}
                    color={colors.gold}
                    solid
                  />
                </>
              ) : (
                <Progress.Bar
                  width={null}
                  height={24}
                  key="progress"
                  borderRadius={8}
                  borderWidth={0}
                  color={colors.primary}
                  progress={progressBarValue == Infinity ? 0 : progressBarValue}
                  style={styles.progressBarStyle}
                />
              )}
            </View>
          )}
        </SafeAreaView>
      </View>
    </>
  );
}

export function AufgabenExerciseText(props) {
  return (
    <AudioContextProvider>
      <Exercises {...props} />
    </AudioContextProvider>
  );
}
